import { z } from "zod";
import { ActionSchema } from "../action";
import { EventType, EventVersion } from "./eventTypes";
import { SuggestedActionQuality } from "@acepoker/api-core";

export const HandResultHandEndStateSchema = z.object({
  totalPotSize: z.number().int(),
  pots: z
    .array(
      z.object({
        eligiblePlayers: z.array(z.number()),
        size: z.number().int(),
      }),
    )
    .default([]),
});
export type HandResultHandEndState = z.infer<typeof HandResultHandEndStateSchema>;

export const EventMetadataSchema = z.object({
  eventId: z.string().uuid(),
  eventType: z.nativeEnum(EventType),
  version: z.nativeEnum(EventVersion),
  timestamp: z.string().datetime({ offset: true }),
});
export type EventMetadata = z.infer<typeof EventMetadataSchema>;

export const PlayerActionSummarySchema = z.object({
  playerId: z.string(),
  actionName: z.string(),
  ev: z.number(),
  strategy: z.number(),
  playerCards: z.string(),
  drillId: z.number().nullable().optional(),
});

export type PlayerActionSummary = z.infer<typeof PlayerActionSummarySchema>;

// PlayerLog schema for events (avoiding circular dependency)
const PlayerLogSchema = z.object({
  id: z.number(),
  playerId: z.string().nullable().optional(),
  drillId: z.number().nullable().optional(),
  tableId: z.string().nullable().optional(),
  handId: z.string(),
  handUuid: z.string(),
  moveId: z.string().max(36).min(36),
  actionName: z.string(),
  actionNameRaw: z.string(),
  ev: z.number(),
  strategy: z.number(),
  amount: z.number().nullable().optional(),
  score: z.number(),
  quality: z
    .enum([
      SuggestedActionQuality.Best,
      SuggestedActionQuality.Blunder,
      SuggestedActionQuality.Correct,
      SuggestedActionQuality.Inaccuracy,
      SuggestedActionQuality.Wrong,
    ])
    .nullable(),
  sessionId: z.string().max(36).min(36),
  playerCards: z.string().default(""),
  communityCards: z.string().default(""),
  suggestedActions: z.array(z.any()).default([]), // Simplified to avoid more dependencies
  handHistory: z.array(ActionSchema).default([]),
  players: z.array(z.any()).default([]), // Simplified to avoid more dependencies
  handState: z.any(), // Simplified to avoid more dependencies
  createdAt: z.string().datetime({ offset: true }),
});

export const HandFinishedEventSchema = z.object({
  eventId: z.string().uuid(),
  eventType: z.literal(EventType.HAND_FINISHED),
  version: z.nativeEnum(EventVersion),
  timestamp: z.string().datetime({ offset: true }),
  handUuid: z.string(),
  tableId: z.string(),
  handId: z.string(),
  ledger: z.array(
    z.object({
      name: z.string().optional().nullable(),
      playerId: z.string(),
      seatNo: z.number().int(),
      stackBeforeHand: z.number().int(),
      stackAfterHand: z.number().int(),
      bot: z.boolean(),
      net: z.number().int(),
      handNet: z.number().int(),
    })
  ),
  createdAt: z.string().datetime({ offset: true }),
  handHistory: z.array(ActionSchema),
  communityCards: z.string(),
  handEndState: HandResultHandEndStateSchema,
  players: z.array(PlayerActionSummarySchema),
  playerLogs: z.array(PlayerLogSchema),
  generalWinBet: z.number().optional(),
});
export type HandFinishedEvent = z.infer<typeof HandFinishedEventSchema>;

// Game event schema (currently only HandFinishedEvent)
export const GameEventSchema = HandFinishedEventSchema;
export type GameEvent = z.infer<typeof GameEventSchema>;
